import React, { useMemo } from 'react';
import { Typography, Chip } from '@mui/material';
import { format } from 'date-fns';
import BigNumber from 'bignumber.js';

import { LogEvent } from 'types';
import { EllipseAddress, Table, TableProps } from 'components';
import { convertWeiToTokens, generateBscScanUrl } from 'utilities';
import { TOKENS } from 'constants/tokens';

interface LogTableProps {
  logs: LogEvent[];
  loading?: boolean;
}

const getEventTypeColor = (eventType: string) => {
  switch (eventType) {
    case 'Deposit':
    case 'DualTokenDeposit':
      return 'success';
    case 'ClaimWithdrawn':
      return 'warning';
    case 'Airdrop':
      return 'info';
    case 'NetworkReward':
      return 'secondary';
    default:
      return 'default';
  }
};

const getEventTypeLabel = (eventType: string) => {
  switch (eventType) {
    case 'Deposit':
      return '质押';
    case 'DualTokenDeposit':
      return '双币质押';
    case 'ClaimWithdrawn':
      return '提现';
    case 'Airdrop':
      return '奖励';
    case 'NetworkReward':
      return '网体奖励';
    default:
      return eventType;
  }
};

const formatAmount = (amount: string) => {
  try {
    return convertWeiToTokens({
      valueWei: new BigNumber(amount),
      token: TOKENS.dl,
      returnInReadableFormat: true,
    });
  } catch {
    return amount;
  }
};

const LogTable: React.FC<LogTableProps> = ({ logs, loading = false }) => {
  const columns = useMemo(
    () => [
      { key: 'type', label: '事件类型', orderable: true, align: 'left' },
      { key: 'user', label: '用户地址', orderable: false, align: 'left' },
      { key: 'amount', label: '金额', orderable: true, align: 'right' },
      { key: 'time', label: '时间', orderable: true, align: 'right' },
      { key: 'block', label: '区块号', orderable: true, align: 'left' },
      { key: 'txHash', label: '交易哈希', orderable: false, align: 'left' },
    ],
    [],
  );

  const cardColumns = useMemo(() => {
    // 移动端显示的列顺序
    return [
      { key: 'type', label: '事件类型', orderable: true, align: 'left' },
      { key: 'amount', label: '金额', orderable: true, align: 'right' },
      { key: 'user', label: '用户地址', orderable: false, align: 'left' },
      { key: 'time', label: '时间', orderable: true, align: 'right' },
      { key: 'block', label: '区块号', orderable: true, align: 'left' },
      { key: 'txHash', label: '交易哈希', orderable: false, align: 'left' },
    ];
  }, []);

  const rows: TableProps['data'] = useMemo(
    () =>
      logs.map((log, index) => [
        {
          key: 'type',
          render: () => (
            <Chip
              label={getEventTypeLabel(log.event)}
              color={getEventTypeColor(log.event) as any}
              size="small"
            />
          ),
          value: log.event,
          align: 'left',
        },
        {
          key: 'user',
          render: () => (
            <Typography
              component="a"
              href={generateBscScanUrl(log.args.user || log.args.referrer || '', 'address')}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              <EllipseAddress address={log.args.user || log.args.referrer || ''} />
            </Typography>
          ),
          value: log.args.user || log.args.referrer || '',
          align: 'left',
        },
        {
          key: 'amount',
          render: () => (
            <Typography variant="body2" fontWeight="medium">
              {log.decoded?.amount || formatAmount(log.args.amount || '0')}
            </Typography>
          ),
          value: log.args.amount || '0',
          align: 'right',
        },
        {
          key: 'time',
          render: () => (
            <Typography variant="body2">
              {format(new Date(log.timestamp * 1000), 'MM-dd HH:mm:ss')}
            </Typography>
          ),
          value: log.timestamp,
          align: 'right',
        },
        {
          key: 'block',
          render: () => (
            <Typography variant="body2">
              {log.blockNumber}
            </Typography>
          ),
          value: log.blockNumber,
          align: 'left',
        },
        {
          key: 'txHash',
          render: () => (
            <Typography
              component="a"
              href={generateBscScanUrl(log.transactionHash, 'tx')}
              target="_blank"
              rel="noreferrer"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              <EllipseAddress address={log.transactionHash} />
            </Typography>
          ),
          value: log.transactionHash,
          align: 'left',
        },
      ]),
    [logs],
  );

  if (loading) {
    return (
      <Typography variant="body2" color="textSecondary">
        加载中...
      </Typography>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        暂无数据
      </Typography>
    );
  }

  return (
    <Table
      columns={columns}
      cardColumns={cardColumns}
      data={rows}
      initialOrder={{
        orderBy: 'time',
        orderDirection: 'desc',
      }}
      rowKeyIndex={5} // txHash index
      isFetching={loading}
    />
  );
};

export default LogTable; 